'use client';
import Breadcrumb from '@ui/Breadcrumb';
import Accordion from '@ui/Accordion';
import Button from '@ui/Button';
import Select from '@ui/Select';
import { FAQTopic, FAQ_TOPIC_QUESTION_MAP } from '@util/maps/faq';
import { getFaqUrl } from '@util/urlHelpers';
import { injectFAQScript } from 'features/schemaMarkups';
import { useEffect, useState } from 'react';
const llorem =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.';

interface FAQProps {
  topic?: FAQTopic;
  q?: string;
}
const FAQPage = ({ topic, q }: FAQProps) => {
  const [currentTopic, setCurrentTopic] = useState<FAQTopic>(
    topic ?? 'Account'
  );
  const [currentQuestion, setCurrentQuestion] = useState<string | undefined>(q);
  useEffect(() => {
    const url = getFaqUrl(currentTopic, currentQuestion);
    const baseUrl = window.location.origin;
    const newUrl = baseUrl + url;
    if (window.location.href !== newUrl) {
      window.history.pushState({}, '', newUrl);
    }
  }, [currentTopic, currentQuestion]);

  const topics = Object.keys(FAQ_TOPIC_QUESTION_MAP) as FAQTopic[];
  const faqs = FAQ_TOPIC_QUESTION_MAP[currentTopic];
  const handleTopicChange = (topic: FAQTopic) => {
    setCurrentQuestion(undefined);
    setCurrentTopic(topic);
  };

  const title = `Frequently Asked Questions${
    currentTopic ? ` - ${currentTopic}` : ''
  }`;

  return (
    <div className="flex w-full max-w-content flex-col px-8 py-20 lg:px-60 lg:py-32">
      <script
        id="schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectFAQScript(FAQ_TOPIC_QUESTION_MAP[currentTopic]) ?? '',
        }}
      />
      <div className="mb-12 flex flex-col gap-12 lg:mb-20">
        <Breadcrumb
          items={[
            { label: 'Help center', href: '/help' },
            {
              label: 'Frequently Asked Questions',
              href: '#',
              current: true,
              currentCss: 'text-brand-black',
            },
          ]}
        />
        <h1 className="text-heading-h4 font-bold md:text-heading-h2">
          {title}
        </h1>
      </div>
      <div className="flex w-full flex-col gap-12 lg:flex-row">
        <div className="w-full lg:max-w-[28rem]">
          <div className="block w-full lg:hidden">
            <Select
              allowEmpty={false}
              value={currentTopic}
              onChange={(value) => handleTopicChange(value.value as FAQTopic)}
              options={topics.map((t) => ({
                label: t,
                value: t,
                id: t,
              }))}
            />
          </div>
          <div className="hidden lg:block">
            <h5 className="mb-12 text-heading-h6 font-bold">Topics</h5>
            <div className="flex flex-col items-start gap-8 text-body-md font-medium">
              {topics.map((topic, i) => (
                <button
                  key={topic + i}
                  className={`transition-colors ${
                    topic === currentTopic ? 'text-brand-primary' : ''
                  }`}
                  onClick={() => handleTopicChange(topic)}
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col gap-20 lg:gap-32">
          <div className="flex flex-col">
            {faqs.map((faq, i) => {
              return (
                <Accordion
                  key={`${currentTopic}${faq.q}${i}`}
                  title={faq.q}
                  divider={true}
                  hover="text"
                  intent="faq"
                  highlightHeaderOnOpen={true}
                  startOpen={currentQuestion === faq.q}
                  onOpen={() => setCurrentQuestion(faq.q)}
                  onClose={() => setCurrentQuestion(undefined)}
                >
                  <div className="pl-[1.6rem]">{faq.a || llorem}</div>
                </Accordion>
              );
            })}
          </div>

          <div className="relative w-full overflow-clip rounded-[0.6rem] bg-brand-black bg-faq-banner bg-cover bg-center py-[6rem]">
            <div className="z-10 flex flex-col items-center text-center text-brand-white">
              <h3 className="mb-4 select-none text-heading-h5 font-bold lg:text-heading-h3">
                Didn&apos;t find an answer to your question?
              </h3>
              <h5 className="mb-12 select-none text-body-md font-medium">
                Get in contact with us!
              </h5>
              <Button
                text="Contact Motorsport Locker"
                type="secondary"
                href="/contact"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
